import { reaction } from 'mobx';
import type { AppModel } from '@/models/AppModel';
import type { LogicGraph } from '@/models/logic/LogicGraph';
import type { LogicNodeInstance } from '@/models/logic/LogicNodeInstance';
import type { LogicSimulation } from '@/models/logic/LogicSimulation';

export interface ActiveExtensionNode {
  node: LogicNodeInstance;
  simulation: LogicSimulation;
  graph: LogicGraph;
}
const indexes = new WeakMap<
  LogicGraph,
  { size: number; types: Map<string, LogicNodeInstance[]> }
>();
function nodesOfType(graph: LogicGraph, type: string) {
  let index = indexes.get(graph);
  if (!index || index.size !== graph.nodes.size) {
    const types = new Map<string, LogicNodeInstance[]>();
    for (const node of graph.nodes.values()) {
      if (!node.type.startsWith('extension:')) continue;
      let nodes = types.get(node.type);
      if (!nodes) types.set(node.type, (nodes = []));
      nodes.push(node);
    }
    indexes.set(graph, (index = { size: graph.nodes.size, types }));
  }
  return index.types.get(type) ?? [];
}

/** Published graphs have stable membership. Index once per graph, shared by all extensions. */
export function watchActiveExtensionNodes(
  app: AppModel,
  type: string | readonly string[],
  update: (nodes: ActiveExtensionNode[]) => void,
) {
  const requestedTypes = typeof type === 'string' ? [type] : type;
  const dispose = reaction(
    () => {
      const context = app.context;
      if (!context) return [];
      const chips = new Set([context.mainChip, context.editor.currentChip]);
      return [...chips].map((chip) => ({
        graph: chip.logicCircuit.logicGraph,
        simulation: chip.logicCircuit.logicSimulation,
      }));
    },
    (graphs) =>
      update(
        graphs.flatMap(({ graph, simulation }) =>
          requestedTypes
            .flatMap((type) => nodesOfType(graph, type))
            .map((node) => ({ node, simulation, graph })),
        ),
      ),
    {
      fireImmediately: true,
      equals: (a, b) =>
        a.length === b.length &&
        a.every(
          (entry, index) =>
            entry.graph === b[index].graph && entry.simulation === b[index].simulation,
        ),
    },
  );
  return () => {
    dispose();
    update([]);
  };
}
